import { toast } from 'react-hot-toast';
import { useState, useEffect, useRef, type SetStateAction } from 'react';
import type { TempCustomization } from '../pages/CustomizeOrder';
import { paymentService } from '../services/payment.service';
import { productService, type OrderChangeResult, type DiscountDetailResult } from '../services/product.service';
import OrderChangeConfirmationModal from './OrderChangeConfirmationModal';
import { useNavigate } from 'react-router-dom';
import DeliveryAddressForm, {
  EMPTY_DELIVERY_ADDRESS,
  type DeliveryAddressForm as DeliveryAddressFormData,
  type DeliveryAddressFormHandle,
  type DeliveryChoice,
  type DeliveryOption,
  type GroupDeliveryAddress,
  mapGroupAddressToForm,
  toDeliveryAddressPayload,
} from './DeliveryAddressForm';
import {
  applyRecommendedToClientAddress,
  formatSendcloudInvalidMessage,
  formatSendcloudStreet,
  isSendcloudAddressValid,
  sendcloudInvalidClientFields,
  pickRecommendedSendcloudAddress,
  recommendedChangesClientForm,
  validateClientAddress,
  type SendcloudRawAddress,
} from '../services/address-validate.service';

/**
 * Builds the initial individual delivery address.
 *
 * Priority:
 *  1. If the user already has a saved individual address (from a previous
 *     order), use it as-is.
 *  2. Otherwise (first-time user or no individual address saved yet), default
 *     the country to 'ES' and prefill name / email / phone from the order
 *     data that is already available in the modal (collected by OrderInfoModal
 *     or loaded from the existing group_order row).
 */
function buildIndividualAddressDefault(
  userDeliveryAddress: GroupDeliveryAddress | undefined,
  orderFallback?: { name?: string; email?: string; phone?: string }
): DeliveryAddressFormData {
  const mapped = mapGroupAddressToForm(userDeliveryAddress);

  // Has a real saved address → use it
  if (userDeliveryAddress && (mapped.recepient_name || mapped.recepient_line_1)) {
    return mapped;
  }

  // No saved address: default to ES + prefill from the order's contact data
  const phone = orderFallback?.phone ?? '';
  return {
    ...EMPTY_DELIVERY_ADDRESS,
    recepient_country: 'ES',
    recepient_name: orderFallback?.name ?? '',
    recepient_email: orderFallback?.email ?? '',
    // Normalise phone: PhoneInput may omit the leading '+'
    recepient_phone: phone
      ? phone.startsWith('+') ? phone : `+${phone}`
      : '',
  };
}

interface OrderSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: {
    id?: string;
    group_id: string;
    name: string;
    email: string;
    phone: string;
    garment_id?: number;
    color_id?: number;
    size_id?: number;
    product_id?: number;
    total_price: number;
    customizations?: TempCustomization[];
    // Campos adicionales para presentación
    garment_name?: string;
    color_name?: string;
    color_hex_code?: string;
    size_name?: string;
    garment_price?: number;
    color_price?: number;
    size_price?: number;
    // Códigos de descuento y precios finales
    discount_codes?: string[];
    total_discount?: number;
    final_price?: number;
    // Cambio de pedido — viene del backend via calculate-price
    is_change?: boolean;
    order_change?: OrderChangeResult | null;
  };
  groupId: string;
  deliveryOption: DeliveryOption;
  deliveryChoice: DeliveryChoice;
  onDeliveryChoiceChange: (choice: DeliveryChoice) => void;
  deliveryPrice: number;
  individualOptionPrice?: number;
  groupDeliveryAddress?: GroupDeliveryAddress;
  userDeliveryAddress?: GroupDeliveryAddress;
  groupedOptionPrice?: number;
  isAddressChangeOnly?: boolean;
}

export default function OrderSummaryModal({
  isOpen,
  onClose,
  order,
  deliveryOption,
  deliveryChoice,
  onDeliveryChoiceChange,
  deliveryPrice,
  individualOptionPrice = 0,
  groupedOptionPrice = 0,
  groupDeliveryAddress,
  userDeliveryAddress,
  isAddressChangeOnly = false,
}: OrderSummaryModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [displayCustomizations, setDisplayCustomizations] = useState<TempCustomization[]>([]);
  const [showChangeConfirmation, setShowChangeConfirmation] = useState(false);
  const deliveryAddressFormRef = useRef<DeliveryAddressFormHandle>(null);
  const navigate = useNavigate();

  // ── Discount code states ────────────────────────────────────────────────────
  const [currentCode, setCurrentCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [codeStatusMessage, setCodeStatusMessage] = useState('');
  const [appliedCodes, setAppliedCodes] = useState<string[]>([]);
  const [discountDetails, setDiscountDetails] = useState<DiscountDetailResult[]>([]);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [finalPrice, setFinalPrice] = useState(Number(order.final_price ?? order.total_price) || 0);
  const [currentDeliveryPrice, setCurrentDeliveryPrice] = useState(deliveryPrice);

  // ── Delivery address states ─────────────────────────────────────────────────────
  const [deliveryAddress, setDeliveryAddressState] = useState<DeliveryAddressFormData>(
    deliveryChoice === 'grouped'
      ? mapGroupAddressToForm(groupDeliveryAddress)
      : buildIndividualAddressDefault(userDeliveryAddress, order)
  );
  const showIndividualOption = deliveryOption === 'individual' || deliveryOption === 'hybrid';
  const showGroupedOption = deliveryOption === 'grouped' || deliveryOption === 'hybrid';
  const needsUserAddress = showIndividualOption && deliveryChoice === 'individual';
  const [checkStatus, setCheckStatus] = useState<'idle' | 'checking' | 'verified' | 'invalid' | 'error' | 'suggest'>('idle');
  const [recommended, setRecommended] = useState<SendcloudRawAddress | null>(null);
  const [invalidMessage, setInvalidMessage] = useState('');
  const [invalidFields, setInvalidFields] = useState<Array<keyof DeliveryAddressFormData>>([]);
  const [addressVerified, setAddressVerified] = useState(false);

  const setDeliveryAddress = (next: SetStateAction<DeliveryAddressFormData>) => {
    setAddressVerified(false);
    setCheckStatus('idle');
    setRecommended(null);
    setDeliveryAddressState(next);
  };

  // Prefill address when the modal opens or delivery choice changes
  useEffect(() => {
    if (!isOpen) return;
    setDeliveryAddressState(
      deliveryChoice === 'grouped'
        ? mapGroupAddressToForm(groupDeliveryAddress)
        : buildIndividualAddressDefault(userDeliveryAddress, order)
    );
    setCheckStatus('idle');
    setRecommended(null);
    setInvalidFields([]);
    setAddressVerified(false);
  }, [isOpen, deliveryChoice, groupDeliveryAddress, userDeliveryAddress]);

  // Sync delivery price when parent prop changes (e.g. modal just opened)
  useEffect(() => {
    if (!isOpen) return;
    setCurrentDeliveryPrice(deliveryPrice);
  }, [isOpen, deliveryPrice]);

  // ── Order change states ─────────────────────────────────────────────────────
  const [isChange] = useState(order.is_change ?? false);
  const [orderChange, setOrderChange] = useState<OrderChangeResult | null>(order.order_change ?? null);

  const adjustedOrderChange = orderChange;
  const displayFinalPrice = finalPrice;

  // ── Sync state when order prop changes ─────────────────────────────────────
  useEffect(() => {
    setFinalPrice(Number(order.final_price ?? order.total_price) || 0);
    setOrderChange(order.order_change ?? null);
    setAppliedCodes([]);
    setDiscountDetails([]);
    setTotalDiscount(0);
    setCodeStatusMessage('');
  }, [order]);

  // ── Build display customizations from order ─────────────────────────────────
  useEffect(() => {
    if (!order.customizations?.length) {
      setDisplayCustomizations([]);
      return;
    }

    const active = order.customizations
      .filter(c => c.value && c.value !== 'false' && c.value !== '' && c.value !== '0')
      .map(c => c.is_locked ? { ...c, price: 0 } : c);

    setDisplayCustomizations(active);
  }, [order.customizations]);

  // ── Helpers ─────────────────────────────────────────────────────────────────

  const formatPrice = (value: number | string): string =>
    `${(Number(value) || 0).toFixed(2)}€`;

  const calculateCustomizationsSubtotal = (): number =>
    displayCustomizations.reduce((sum, item) => sum + (Number(item.price) || 0), 0);

  // ── Builds the customizations list for calculate-price calls ─────────────────
  const buildCustomizationsForApi = () =>
    (order.customizations ?? [])
      .filter(c => !c.is_locked && c.value && c.value !== 'false' && c.value.trim() !== '')
      .map(c => ({ option_id: c.option_id, value: c.value }));

  const handleDeliveryChoiceChange = async (choice: DeliveryChoice) => {
    if (choice === deliveryChoice) return;
    onDeliveryChoiceChange(choice);

    if (!order.product_id) {
      setCurrentDeliveryPrice(choice === 'individual' ? individualOptionPrice : 0);
      return;
    }

    const result = await productService.calculatePrice(order.group_id, {
      product_id: order.product_id,
      customizations: buildCustomizationsForApi(),
      previous_order_id: isChange && order.id ? order.id : null,
      discount_codes: appliedCodes,
      delivery_choice: choice,
    });

    if (!result) {
      setCurrentDeliveryPrice(choice === 'individual' ? individualOptionPrice : 0);
      return;
    }

    setCurrentDeliveryPrice(
      Number(result.breakdown.delivery_price) || (choice === 'individual' ? individualOptionPrice : 0)
    );
    setDiscountDetails(result.discount_details ?? []);
    setTotalDiscount(Number(result.breakdown.discount) || 0);
    setFinalPrice(Number(result.breakdown.total) || 0);
    setOrderChange(result.order_change);
  };

  // ── Recalculate price whenever applied codes change ──────────────────────────
  const recalculateWithCodes = async (codes: string[]) => {
    if (!order.product_id) return;

    const result = await productService.calculatePrice(order.group_id, {
      product_id: order.product_id,
      customizations: buildCustomizationsForApi(),
      previous_order_id: isChange && order.id ? order.id : null,
      discount_codes: codes,
      delivery_choice: deliveryChoice,
    });

    if (!result) return;

    const details = result.discount_details ?? [];
    setDiscountDetails(details);
    setTotalDiscount(Number(result.breakdown.discount) || 0);
    setFinalPrice(Number(result.breakdown.total) || 0);
    setCurrentDeliveryPrice(
      Number(result.breakdown.delivery_price) || (deliveryChoice === 'individual' ? individualOptionPrice : 0)
    );
    setOrderChange(result.order_change);
  };

  // ── Apply a discount code ─────────────────────────────────────────────────────
  const handleApplyCode = async () => {
    const code = currentCode.trim().toUpperCase();
    if (!code) return;

    if (appliedCodes.includes(code)) {
      setCodeStatusMessage('Este código ya está aplicado');
      return;
    }

    try {
      setIsValidating(true);
      setCodeStatusMessage('');

      const tentativeCodes = [...appliedCodes, code];

      const result = await productService.calculatePrice(order.group_id, {
        product_id: order.product_id!,
        customizations: buildCustomizationsForApi(),
        previous_order_id: isChange && order.id ? order.id : null,
        discount_codes: tentativeCodes,
        delivery_choice: deliveryChoice,
      });

      if (!result) {
        setCodeStatusMessage('Error al validar el código');
        return;
      }

      const detail = result.discount_details?.find(d => d.code === code);

      if (!detail) {
        setCodeStatusMessage('Código no válido o expirado');
        return;
      }

      setAppliedCodes(tentativeCodes);
      setDiscountDetails(result.discount_details ?? []);
      setTotalDiscount(Number(result.breakdown.discount) || 0);
      setFinalPrice(Number(result.breakdown.total) || 0);
      setCurrentDeliveryPrice(
        Number(result.breakdown.delivery_price) || (deliveryChoice === 'individual' ? individualOptionPrice : 0)
      );
      setOrderChange(result.order_change);
      setCurrentCode('');
      setCodeStatusMessage(`✓ ${code}: -${(Number(detail.amount) || 0).toFixed(2)}€`);
    } catch {
      setCodeStatusMessage('Error al validar el código');
    } finally {
      setIsValidating(false);
    }
  };

  const handleRemoveCode = async (codeToRemove: string) => {
    const remaining = appliedCodes.filter(c => c !== codeToRemove);
    setAppliedCodes(remaining);
    setCodeStatusMessage('');
    await recalculateWithCodes(remaining);
  };

  // ── Confirm order / go to payment ────────────────────────────────────────────
  const handleConfirmOrder = async () => {
    try {
      setErrorMessage(null);

      if (needsUserAddress && !deliveryAddressFormRef.current?.validate()) {
        document.getElementById('delivery-address-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      if (needsUserAddress && !addressVerified) {
        const alreadySawSuggest = checkStatus === 'suggest';
        setCheckStatus('checking');
        try {
          const result = await validateClientAddress(deliveryAddress);
          const suggestion = pickRecommendedSendcloudAddress(result);
          if (suggestion && recommendedChangesClientForm(deliveryAddress, suggestion) && !alreadySawSuggest) {
            setRecommended(suggestion);
            setCheckStatus('suggest');
            document.getElementById('delivery-address-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
          if (!isSendcloudAddressValid(result)) {
            setRecommended(null);
            setInvalidMessage(formatSendcloudInvalidMessage(result));
            setInvalidFields(sendcloudInvalidClientFields(result));
            setCheckStatus('invalid');
            document.getElementById('delivery-address-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
          setRecommended(null);
          setCheckStatus('verified');
          setAddressVerified(true);
        } catch {
          setCheckStatus('error');
          toast.error('No se pudo comprobar la dirección');
          return;
        }
      }

      setIsLoading(true);
      toast.loading('Iniciando proceso de pago...', { id: 'payment-toast' });

      if (!order.product_id) {
        throw new Error('No se ha seleccionado un producto válido');
      }

      // Build customizations for payment service
      const formattedCustomizations = [];

      if (order.customizations?.length) {
        for (const c of order.customizations.filter(c => c.is_locked)) {
          formattedCustomizations.push({
            option_id: c.option_id,
            value: c.value || 'false',
            custom_value: c.custom_value,
            is_locked: true,
          });
        }

        const validNormal = order.customizations.filter(
          c => !c.is_locked && c.value && c.value !== 'false' && c.value !== '' && c.value !== '0'
        );
        for (const c of validNormal) {
          formattedCustomizations.push({
            option_id: c.option_id,
            value: c.value.toString(),
            custom_value: c.custom_value,
          });
        }
      }

      const paymentData = {
        name: order.name,
        email: order.email,
        phoneNumber: order.phone,
        groupId: order.group_id,
        productId: order.product_id,
        totalPrice: finalPrice,
        customizations: formattedCustomizations,
        discountCodes: appliedCodes,
        deliveryOption,
        deliveryChoice,
        // Only send a personal address for individual shipping; grouped uses the group's address.
        ...(needsUserAddress
          ? { deliveryAddress: toDeliveryAddressPayload(deliveryAddress) }
          : {}),
        ...(order.id ? { previousOrderId: order.id } : {}),
      };

      const paymentResponse = await paymentService.createPayment(paymentData);

      if (!paymentResponse?.Ds_MerchantParameters) {
        throw new Error('Error al procesar el pago. Por favor, inténtalo de nuevo.');
      }

      const isCollectivePayment = paymentResponse.Ds_SignatureVersion === 'COLLECTIVE';

      localStorage.setItem('pendingOrder', JSON.stringify({
        ...order,
        discount_codes: appliedCodes,
        total_discount: totalDiscount,
        final_price: displayFinalPrice,
        order_change: adjustedOrderChange,
        delivery_option: deliveryOption,
        delivery_choice: deliveryChoice,
        ...(needsUserAddress
          ? { delivery_address: toDeliveryAddressPayload(deliveryAddress) }
          : {}),
        // Create already stored the order. Omit the id so the success page
        // does not also call /api/payments/register.
        redsys_order_id: isCollectivePayment ? null : (paymentResponse.order_id || null),
        timestamp: new Date().toISOString(),
      }));

      toast.dismiss('payment-toast');

      if (isCollectivePayment) {
        toast.success('Pedido confirmado');
        navigate('/payment/success');
        return;
      }

      toast.success('Redirigiendo a la pasarela de pago...');
      setTimeout(() => {
        paymentService.redirectToRedsys(paymentResponse);
      }, 1000);
    } catch (error: any) {
      toast.dismiss('payment-toast');

      let errorMsg = error.message || 'Error al iniciar el proceso de pago';
      if (errorMsg.includes('404')) {
        errorMsg = 'El servicio de pago no está disponible en este momento. Por favor, inténtalo más tarde.';
      } else if (errorMsg.includes('JSON')) {
        errorMsg = 'Se produjo un error al procesar la información. Por favor, inténtalo más tarde.';
      }

      toast.error(errorMsg);
      setErrorMessage(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[99999]">
      <div className="bg-white rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="flex items-center justify-between mb-5 pb-3 border-b">
          <h2 className="text-2xl font-bold text-gray-800">Resumen del Pedido</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-5">
          {/* Error message */}
          {errorMessage && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              <p className="font-medium">Error:</p>
              <p>{errorMessage}</p>
              <p className="mt-1 text-xs">Si el problema persiste, por favor contacta con soporte.</p>
            </div>
          )}

          {/* Contact info */}
          {!isAddressChangeOnly && (
            <div>
              <h3 className="text-xs uppercase font-semibold text-gray-500 mb-2">Información de contacto</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-xs text-gray-500">Nombre</span>
                    <p className="font-medium text-sm">{order.name}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">Teléfono</span>
                    <p className="font-medium text-sm">{order.phone}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-xs text-gray-500">Email</span>
                    <p className="font-medium text-sm">{order.email}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Product details */}
          {!isAddressChangeOnly && (
            <div className="border-b pb-4">
              <h3 className="font-medium text-sm text-gray-800 mb-3">Detalles del producto</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">Prenda base: {order.garment_name}</span>
                  <span className="font-medium">{formatPrice(order.garment_price ?? 0)}</span>
                </div>

                <div className="flex justify-between text-xs">
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-600">Color: {order.color_name}</span>
                    {order.color_hex_code && (
                      <div
                        className="w-3 h-3 rounded-full border border-gray-300"
                        style={{ backgroundColor: order.color_hex_code }}
                      />
                    )}
                  </div>
                  {Number(order.color_price ?? 0) > 0 && (
                    <span className="font-medium">{formatPrice(order.color_price ?? 0)}</span>
                  )}
                </div>

                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">Talla: {order.size_name}</span>
                  {Number(order.size_price ?? 0) > 0 && (
                    <span className="font-medium">{formatPrice(order.size_price ?? 0)}</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Customizations */}
          {displayCustomizations.length > 0 && (
            <div className="border-b pb-4">
              <h3 className="font-medium text-sm text-gray-800 mb-3">Personalizaciones</h3>
              <div className="space-y-2">
                {displayCustomizations.map((customization, index) => (
                  <div key={index} className="flex justify-between text-xs">
                    <span className="text-gray-600">
                      {customization.option?.name}
                      {customization.value !== 'true' && (
                        <span className="text-gray-500 ml-1">({customization.value})</span>
                      )}
                    </span>
                    {Number(customization.price ?? 0) > 0 ? (
                      <span className="font-medium text-red-600">
                        +{formatPrice(customization.price ?? 0)}
                      </span>
                    ) : (
                      <span className="text-gray-400 italic text-xs">Incluido</span>
                    )}
                  </div>
                ))}

                {displayCustomizations.length > 1 && (
                  <div className="flex justify-between text-xs mt-3 pt-2 border-t border-dashed border-gray-200">
                    <span className="font-medium text-gray-700">Subtotal personalizaciones</span>
                    <span className="font-medium">{formatPrice(calculateCustomizationsSubtotal())}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Discount codes */}
          <div className="border-b pb-4">
            <h3 className="font-medium text-sm text-gray-800 mb-3">Códigos de descuento</h3>

            {appliedCodes.length > 0 && (
              <div className="mb-3 space-y-2">
                {appliedCodes.map((code, index) => {
                  const detail = discountDetails.find(d => d.code === code);
                  return (
                    <div key={index} className="flex items-center justify-between bg-green-50 border border-green-200 rounded px-3 py-2">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-green-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-xs font-medium text-green-700">
                          {code}
                          {detail && ` (-${(Number(detail.amount) || 0).toFixed(2)}€)`}
                        </span>
                      </div>
                      <button
                        onClick={() => handleRemoveCode(code)}
                        className="text-green-600 hover:text-green-800 transition-colors"
                        title="Quitar código"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={currentCode}
                  onChange={e => {
                    setCurrentCode(e.target.value.toUpperCase());
                    setCodeStatusMessage('');
                  }}
                  onKeyPress={e => e.key === 'Enter' && handleApplyCode()}
                  placeholder="Introducir código"
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
                <button
                  onClick={handleApplyCode}
                  disabled={!currentCode.trim() || isValidating}
                  className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
                    !currentCode.trim() || isValidating
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                >
                  {isValidating ? (
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : 'Aplicar'}
                </button>
              </div>

              {codeStatusMessage && (
                <div className={`text-xs ${codeStatusMessage.startsWith('✓') ? 'text-green-600' : 'text-red-600'}`}>
                  {codeStatusMessage}
                </div>
              )}

              <div className="mt-3 p-3 bg-gradient-to-r from-red-50 to-pink-50 border border-red-100 rounded-lg text-center">
                <p className="text-xs text-red-700 font-semibold mb-1">
                  ¿AÚN NO TIENES TU CÓDIGO DE DESCUENTO?
                </p>
                <p className="text-xs text-gray-600">
                  Síguenos en{' '}
                  <a
                    href="https://www.instagram.com/fraeternity/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-red-600 hover:text-red-700 underline"
                  >
                    Instagram
                  </a>
                  {' '}y/o{' '}
                  <a
                    href="https://www.tiktok.com/@fraeternitytok"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-red-600 hover:text-red-700 underline"
                  >
                    TikTok
                  </a>
                  {' '}y escríbenos
                </p>
                <p className="text-xs text-gray-600">
                  para conseguir <span className="font-bold text-red-700">los DOS descuentos</span>!
                </p>
              </div>
            </div>
          </div>

          {/* Delivery method + address */}
          <div className="border-b pb-4">
            <h3 className="font-medium text-sm text-gray-800 mb-3">
              {!showIndividualOption ? 'Tu pedido se entregará en la dirección del grupo facilitada por el organizador antes del envío.' : '¿Dónde quieres recibir tu pedido?'}
            </h3>

            <div className="space-y-3 mb-4">
              {showIndividualOption && (
                <label
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    deliveryChoice === 'individual'
                      ? 'border-red-500 bg-red-50/40'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="modal-delivery-choice"
                    checked={deliveryChoice === 'individual'}
                    onChange={() => handleDeliveryChoiceChange('individual')}
                    className="mt-1 h-4 w-4 text-[#F90000] border-gray-300 focus:ring-[#F90000]"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-gray-800">
                        Entrega individual (dirección particular)
                      </span>
                      {individualOptionPrice !== undefined && individualOptionPrice !== null && (
                        <span className="text-xs font-medium text-[#F90000] whitespace-nowrap">
                          +{formatPrice(individualOptionPrice)}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Tu prenda se enviará individualmente a la dirección que indiques, por separado del resto de tu grupo.
                    </p>
                  </div>
                </label>
              )}

              {showGroupedOption && (
                <label
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    deliveryChoice === 'grouped'
                      ? 'border-red-500 bg-red-50/40'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="modal-delivery-choice"
                    checked={deliveryChoice === 'grouped'}
                    onChange={() => handleDeliveryChoiceChange('grouped')}
                    className="mt-1 h-4 w-4 text-[#F90000] border-gray-300 focus:ring-[#F90000]"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-gray-800">Entrega grupal</span>
                      {groupedOptionPrice !== undefined && groupedOptionPrice !== null && (
                        <span className="text-xs font-medium text-[#F90000] whitespace-nowrap">
                          +{formatPrice(groupedOptionPrice)}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Tus prendas se enviarán junto con los demás pedidos que hayan seleccionado envío grupal, a la dirección que facilite el organizador antes del envío.
                    </p>
                  </div>
                </label>
              )}
            </div>

            {needsUserAddress && (
              <>
                <h4 className="text-xs font-semibold uppercase text-gray-500 mb-3">
                  Dirección de entrega
                </h4>
                <DeliveryAddressForm
                  ref={deliveryAddressFormRef}
                  deliveryAddress={deliveryAddress}
                  setDeliveryAddress={setDeliveryAddress}
                  highlightFields={invalidFields}
                  onClearHighlight={(field) =>
                    setInvalidFields((prev) => prev.filter((item) => item !== field))
                  }
                  restrictToSpainIndividual
                />
                {checkStatus === 'checking' && (
                  <p className="text-sm text-gray-600 mt-3">Comprobando la dirección…</p>
                )}
                {checkStatus === 'verified' && (
                  <p className="text-sm text-green-700 mt-3">Dirección verificada</p>
                )}
                {checkStatus === 'error' && (
                  <p className="text-sm text-red-600 mt-3">No se pudo comprobar la dirección</p>
                )}
                {checkStatus === 'invalid' && (
                  <p className="text-sm text-amber-900 mt-3">{invalidMessage}</p>
                )}
                {checkStatus === 'suggest' && recommended && (
                  <div className="mt-3 space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                    <p className="text-sm font-medium text-amber-900">Dirección sugerida</p>
                    <p className="text-sm text-gray-700">{formatSendcloudStreet(recommended)}</p>
                    <button
                      type="button"
                      onClick={() => {
                        setDeliveryAddressState(applyRecommendedToClientAddress(deliveryAddress, recommended));
                        setRecommended(null);
                        setCheckStatus('idle');
                        setAddressVerified(false);
                      }}
                      className="px-3 py-1.5 rounded-lg border border-amber-300 text-sm font-medium text-amber-900 hover:bg-amber-100"
                    >
                      Usar esta dirección
                    </button>
                    <p className="text-xs text-gray-500">O continúa para mantener la tuya si es válida.</p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Totals */}
          <div className="mt-4">
            <div className="space-y-2">
              {/* Order change info — only show prices when the new order costs more */}
              {isChange && adjustedOrderChange && adjustedOrderChange.requires_payment && Number(adjustedOrderChange.price_difference) > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Pedido anterior</span>
                    <span className="text-gray-400 line-through">{formatPrice(adjustedOrderChange.net_paid_amount)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Nuevo pedido</span>
                    <span className="text-gray-700 font-medium">{formatPrice(adjustedOrderChange.new_price)}</span>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                    <span className="text-sm font-medium text-gray-700">A pagar</span>
                    <span className="text-base font-bold text-[#F90000]">+{formatPrice(Math.abs(adjustedOrderChange.price_difference))}</span>
                  </div>
                </div>
              )}

              {/* Change with no extra payment — just a notice */}
              {isChange && adjustedOrderChange && !adjustedOrderChange.requires_payment && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm text-gray-600">
                      {adjustedOrderChange.requires_refund
                        ? `Se realizarán los cambios. Diferencia a favor: ${formatPrice(Math.abs(adjustedOrderChange.price_difference))}.`
                        : 'Se realizarán los cambios en tu pedido sin coste adicional.'}
                    </span>
                  </div>
                </div>
              )}

              {/* Subtotal (solo si no es cambio) */}
              {!isChange && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span>{formatPrice(order.total_price)}</span>
                </div>
              )}

              {!isChange && currentDeliveryPrice > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Entrega</span>
                  <span>{formatPrice(currentDeliveryPrice)}</span>
                </div>
              )}

              {/* Applied discounts */}
              {appliedCodes.length > 0 && totalDiscount > 0 && (
                <div className="space-y-1">
                  <div className="flex justify-between text-sm font-medium text-green-700">
                    <span>Descuentos aplicados:</span>
                  </div>
                  {discountDetails.map((detail, index) => (
                    <div key={index} className="flex justify-between text-xs text-green-600 ml-2">
                      <span>• {detail.code}</span>
                      <span>-{formatPrice(detail.amount)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm text-green-600 font-medium">
                    <span>Total descuentos</span>
                    <span>-{formatPrice(totalDiscount)}</span>
                  </div>
                </div>
              )}

              {/* Final total */}
              {(!adjustedOrderChange || adjustedOrderChange.requires_payment) && (
                <div className="flex justify-between items-center py-3 border-t border-b">
                  <span className="font-semibold text-gray-800">Total</span>
                  <span className="font-bold text-xl text-red-600">
                    {isChange && adjustedOrderChange
                      ? adjustedOrderChange.requires_payment
                        ? formatPrice(Math.abs(adjustedOrderChange.price_difference))
                        : formatPrice(0)
                      : formatPrice(finalPrice)
                    }
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3 mt-6">
            <button
              type="button"
              onClick={handleConfirmOrder}
              disabled={isLoading || checkStatus === 'checking'}
              className={`w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-md text-base font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors ${isLoading || checkStatus === 'checking' ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isLoading || checkStatus === 'checking' ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>{checkStatus === 'checking' ? 'Comprobando…' : 'Procesando...'}</span>
                </>
              ) : (
                isChange ? 'Realizar cambios' : 'Confirmar pedido'
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className={`w-full py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>

      {/* Modal de confirmación de cambio */}
      <OrderChangeConfirmationModal
        isOpen={showChangeConfirmation}
        onClose={() => {
          setShowChangeConfirmation(false);
          onClose(); // También cerrar el modal principal
          navigate('/');
        }}
        orderInfo={{
          name: order.name,
          email: order.email,
          garment_name: order.garment_name,
          color_name: order.color_name,
          color_hex_code: order.color_hex_code,
          size_name: order.size_name,
          previous_order_price: adjustedOrderChange?.net_paid_amount,
          final_price: isChange && adjustedOrderChange ? adjustedOrderChange.new_price : displayFinalPrice,
          price_difference: adjustedOrderChange?.price_difference,
          total_discount: totalDiscount
        }}
      />
    </div>
  );
}
